<!--
  @component
  Implement a panel that allows searching media files from an external media library and selecting
  one for an image/file entry field.
-->
<script>
  import { _ } from '@sveltia/i18n';
  import { Alert, EmptyState, InfiniteScroll, Toast } from '@sveltia/ui';
  import { sleep } from '@sveltia/utils/misc';
  import { onMount } from 'svelte';

  import AssetPath from '$lib/components/assets/browser/asset-path.svelte';
  import PickerBreadcrumb from '$lib/components/assets/browser/picker-breadcrumb.svelte';
  import SimpleImageGridItem from '$lib/components/assets/browser/simple-image-grid-item.svelte';
  import SimpleImageGrid from '$lib/components/assets/browser/simple-image-grid.svelte';
  import SubfolderStrip from '$lib/components/assets/browser/subfolder-strip.svelte';
  import SubfolderNameDialog from '$lib/components/assets/list/subfolder-name-dialog.svelte';
  import AssetPreview from '$lib/components/assets/shared/asset-preview.svelte';
  import CloudServiceAuth from '$lib/components/assets/shared/cloud-service-auth.svelte';
  import DropZone from '$lib/components/assets/shared/drop-zone.svelte';
  import RejectedFilesAlertDialog from '$lib/components/assets/shared/rejected-files-alert-dialog.svelte';
  import { getSelectionWithResource } from '$lib/services/assets/browser/select-assets-dialog.svelte';
  import { getFetchOptions, mergeUploadedExternalAssets } from '$lib/services/assets/external';
  import {
    fetchExternalAssetBlob,
    getSharedMediaLibraryOptions,
    prepareExternalUploads,
  } from '$lib/services/assets/external/data';
  import {
    getExternalAssetsInDir,
    getExternalFolderLabel,
    getExternalSubfolders,
  } from '$lib/services/assets/external/view';
  import { getRelativePath, getTakenNames } from '$lib/services/assets/subfolders';
  import { selectAssetsView } from '$lib/services/contents/editor';
  import { env } from '$lib/services/user/env.svelte';
  import { watch } from '$lib/services/utils/state.svelte';

  /**
   * @import {
   * ExternalAsset,
   * MediaLibraryAssetKind,
   * MediaLibraryFetchOptions,
   * MediaLibraryService,
   * SelectedResource,
   * } from '$lib/types/private';
   * @import { MediaField } from '$lib/types/public';
   */

  /**
   * @typedef {object} Props
   * @property {MediaField} [fieldConfig] File/Image field configuration.
   * @property {MediaLibraryAssetKind} [kind] Asset kind.
   * @property {boolean} [multiple] Whether to allow selecting multiple assets.
   * @property {string} [searchTerms] Search terms for filtering assets.
   * @property {MediaLibraryService} serviceProps Media library service details.
   * @property {string} [gridId] The `id` attribute of the inner listbox.
   * @property {SelectedResource[]} selectedResources Selected resources.
   */

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    kind,
    fieldConfig = undefined,
    multiple = false,
    searchTerms = '',
    serviceProps,
    gridId = undefined,
    selectedResources = $bindable([]),
    /* eslint-enable prefer-const */
  } = $props();

  const {
    serviceType = 'stock_assets',
    serviceId = '',
    serviceLabel = '',
    hotlinking = false,
    authType = 'api_key',
    init,
    list,
    browse,
    search,
    upload,
    createFolder,
  } = $derived(serviceProps);

  // Use the grid view for Picsum as it doesn’t provide description for the assets, and the list
  // view relies on the description to show asset information.
  const viewType = $derived(serviceId === 'picsum' ? 'grid' : selectAssetsView.current?.type);
  const isStockAssets = $derived(serviceType === 'stock_assets');
  const allMediaLibraryOptions = $derived(getSharedMediaLibraryOptions(fieldConfig));
  /* v8 ignore start -- only read to report a file exceeding the configured size */
  const maxSize = $derived(
    /** @type {number} */ (allMediaLibraryOptions.max_file_size ?? Infinity),
  );
  /* v8 ignore stop */

  let hasConfig = $state(true);
  let hasAuthInfo = $state(false);
  let apiKey = $state('');
  let userName = $state('');
  let password = $state('');
  /**
   * Assets listed by the service. Only ever replaced as a whole, so the list, which can run to
   * thousands of files on a cloud storage service, isn’t wrapped in a deep proxy.
   * @type {ExternalAsset[] | null}
   */
  let listedAssets = $state.raw(null);
  /**
   * Paths of the empty folders on a service with folder support, each kept by a placeholder.
   * @type {string[]}
   */
  let folders = $state([]);
  /**
   * Path of the folder being browsed on a service with folder support, relative to the configured
   * prefix. Empty at the root.
   */
  let dirPath = $state('');
  let newFolderDialogOpen = $state(false);
  let folderCreationFailed = $state(false);
  /** @type {string | undefined} */
  let error = $state();
  /** @type {{ show: boolean, status: 'info' | 'error', length: number }} */
  let uploadingToast = $state({ show: false, status: 'info', length: 0 });
  /** @type {RejectedFilesAlertDialog | undefined} */
  let rejectedFilesAlert = $state();

  /** @type {MediaLibraryFetchOptions} */
  const listFetchOptions = $derived({ kind, fieldConfig, apiKey, userName, password });
  /**
   * Search query. The search terms are shared by all the services in the dialog, so a service that
   * can’t search, like Lorem Picsum, ignores the terms entered for another service.
   */
  const searchQuery = $derived(search ? searchTerms.trim() : '');
  /**
   * Whether the service is browsed folder by folder, like a repository folder in the picker. A
   * search looks through the whole service instead, listing the matches with their paths.
   */
  const browsing = $derived(!!browse && !searchQuery);
  /**
   * Assets shown in the panel: those right in the folder being browsed, or every asset listed.
   * @type {ExternalAsset[]}
   */
  const panelAssets = $derived.by(() => {
    const assets = listedAssets ?? [];

    return browsing ? getExternalAssetsInDir({ dirPath, assets }) : assets;
  });
  const subfolders = $derived(
    browsing ? getExternalSubfolders({ dirPath, assets: listedAssets ?? [], folders }) : [],
  );
  /** Names already taken in the folder being browsed, which a new folder can’t be given. */
  const takenNames = $derived(
    getTakenNames({ subfolders, fileNames: panelAssets.map(({ fileName }) => fileName) }),
  );
  /** The folder being browsed, named after the service at the root. */
  const folderLabel = $derived(getExternalFolderLabel({ dirPath, serviceLabel }));

  /**
   * ID of the latest {@link getAssets} request. A request made earlier, e.g. a search for the terms
   * typed before, can come back after a later one, and its result must not replace the latest one.
   */
  let latestRequestId = 0;

  /**
   * Search or list assets from the external media library.
   * @param {string} query Search query, which is only given to a service that can search.
   */
  const getAssets = async (query) => {
    latestRequestId += 1;

    const requestId = latestRequestId;

    listedAssets = null;
    error = undefined;

    try {
      /** @type {ExternalAsset[]} */
      let assets;
      /** @type {string[] | undefined} */
      let emptyFolders;

      if (query) {
        assets = await /** @type {NonNullable<typeof search>} */ (search)(query, listFetchOptions);
      } else if (browse) {
        // A service with folder support lists its empty folders along with the files
        ({ assets, folders: emptyFolders } = await browse(listFetchOptions));
      } else {
        assets = (await list?.(listFetchOptions)) ?? [];
      }

      if (requestId !== latestRequestId) {
        return;
      }

      listedAssets = assets;

      if (emptyFolders) {
        folders = emptyFolders;
      }
    } catch (ex) {
      if (requestId !== latestRequestId) {
        return;
      }

      error = 'search_fetch_failed';
      // eslint-disable-next-line no-console
      console.error(ex);
    }
  };

  /**
   * Download the selected asset, if needed, and return the file and credit. If hotlinking is
   * required by the service, just return the URL instead of downloading the file.
   * @param {ExternalAsset} asset Selected asset.
   * @returns {Promise<SelectedResource | undefined>} The selected resource with the file or URL.
   * @todo Support video files.
   */
  const getResource = async (asset) => {
    const { downloadURL: url, fileName, credit } = asset;

    if (hotlinking) {
      return { url, credit };
    }

    try {
      const blob = await fetchExternalAssetBlob(asset);

      return { url, credit, file: new File([blob], fileName, { type: blob.type }) };
    } catch (ex) {
      error = 'image_fetch_failed';
      // eslint-disable-next-line no-console
      console.error(ex);
    }

    return undefined;
  };

  /**
   * Handle `Drop` event to upload files.
   * @param {File[]} files Dropped files.
   */
  export const uploadFiles = async (files) => {
    if (!upload) {
      return;
    }

    const prepared = await prepareExternalUploads(files, allMediaLibraryOptions);

    files = prepared.validFiles;
    rejectedFilesAlert?.report(prepared);

    if (!files.length) {
      return;
    }

    uploadingToast = { show: true, status: 'info', length: files.length };

    try {
      // The files go to the folder being browsed, or the one the search started from
      const uploaded = await upload(files, { ...listFetchOptions, dirPath });
      const resources = await Promise.all(uploaded.map((asset) => getResource(asset)));

      selectedResources = resources.filter((r) => !!r).slice(0, multiple ? undefined : 1);
      listedAssets = mergeUploadedExternalAssets(uploaded, listedAssets ?? []);
    } catch {
      uploadingToast = { show: true, status: 'error', length: files.length };
    }
  };

  /**
   * Whether a folder can be created in the folder being browsed, which takes a service with folder
   * support that can create one, while it’s browsed rather than searched.
   * @returns {boolean} Result.
   */
  export const canCreateFolder = () => browsing && !!createFolder;

  /**
   * Open the New Folder dialog.
   */
  export const showNewFolderDialog = () => {
    newFolderDialogOpen = true;
  };

  /**
   * Create a folder in the folder being browsed, which is then listed along with the others.
   * @param {string} name Folder name.
   */
  const createNewFolder = async (name) => {
    const path = dirPath ? `${dirPath}/${name}` : name;

    try {
      await /** @type {NonNullable<typeof createFolder>} */ (createFolder)(path, listFetchOptions);
      folders = [...folders, path];
    } catch (ex) {
      folderCreationFailed = true;
      // eslint-disable-next-line no-console
      console.error(ex);
    }
  };

  /**
   * URLs of the selected resources, built once per selection change so that each listed item can
   * check its own selection without going through the whole selection.
   */
  const selectedURLs = $derived(new Set(selectedResources.map(({ url }) => url)));

  /**
   * Check if the given asset is already selected.
   * @param {ExternalAsset} asset The asset to check.
   * @returns {boolean} `true` if the asset is selected, `false` otherwise.
   */
  const isSelected = (asset) => selectedURLs.has(asset.downloadURL);

  /**
   * The latest selection state the list box reported for each asset, keyed by download URL. The
   * resource of a selected asset is only ready after an `await`, by which time the asset may have
   * been deselected: the list box reports the asset that loses a single selection after the one
   * that gets it, if it comes later in the list, and the user can move on while a file downloads.
   * @type {Map<string, boolean>}
   */
  // eslint-disable-next-line svelte/prefer-svelte-reactivity
  const requestedSelection = new Map();

  /**
   * Handle selection change of an asset.
   * @param {ExternalAsset} asset The asset whose selection changed.
   * @param {boolean} selected `true` if the asset is now selected, `false` otherwise.
   */
  const onSelectionChange = async (asset, selected) => {
    const { downloadURL } = asset;

    if (selected && !multiple) {
      // A new single selection replaces the earlier one, including a pick hidden by a search or
      // left in another folder, which the list box doesn’t deselect, and a pick still downloading
      [...requestedSelection.keys()].forEach((url) => {
        requestedSelection.set(url, false);
      });
    }

    requestedSelection.set(downloadURL, selected);

    if (selected) {
      const resource = await getResource(asset);

      // Read the selection again, as it may have changed during the `await`
      if (resource && requestedSelection.get(downloadURL)) {
        selectedResources = getSelectionWithResource({
          resources: selectedResources,
          resource,
          /**
           * Check if a selected resource comes from the same asset.
           * @param {SelectedResource} r Selected resource.
           * @returns {boolean} Result.
           */
          isSame: (r) => r.url === downloadURL,
          multiple,
        });
      }
    } else {
      selectedResources = selectedResources.filter((r) => r.url !== downloadURL);
    }
  };

  /**
   * Load the stored credentials. Fetching the assets is left to the effect below, which reacts to
   * `hasAuthInfo` being set.
   */
  const loadAuthInfo = () => {
    const options = getFetchOptions(serviceProps);

    apiKey = options.apiKey;
    userName = options.userName;
    password = options.password;
    hasAuthInfo = authType === 'none' || !!apiKey || !!password;
  };

  onMount(() => {
    (async () => {
      if (typeof init === 'function') {
        hasConfig = false;
        hasConfig = await init();
      }

      if (!hasConfig) {
        return;
      }

      loadAuthInfo();
      listedAssets = null;
    })();
  });

  watch(
    () => [searchQuery, hasAuthInfo],
    () => {
      if (hasAuthInfo) {
        getAssets(searchQuery);
      }
    },
  );
</script>

{#snippet breadcrumb()}
  {#if browsing}
    <PickerBreadcrumb
      rootLabel={serviceLabel}
      path={dirPath}
      onNavigate={(path) => {
        dirPath = path;
      }}
    />
  {/if}
{/snippet}

{#snippet content()}
  {#if !listedAssets}
    <EmptyState>
      <span role="alert">{_(searchQuery ? 'searching' : 'loading')}</span>
    </EmptyState>
  {:else if !panelAssets.length && !subfolders.length}
    {@render breadcrumb()}
    <EmptyState>
      <span role="alert">{_('no_files_found')}</span>
    </EmptyState>
  {:else}
    {@render breadcrumb()}
    <div role="none" class="grid-wrapper">
      {#if subfolders.length}
        <SubfolderStrip
          {subfolders}
          {viewType}
          onOpen={({ path }) => {
            dirPath = path;
          }}
        />
      {/if}
      <SimpleImageGrid {viewType} {gridId} {multiple}>
        <InfiniteScroll items={panelAssets} itemKey="id">
          {#snippet renderItem(/** @type {ExternalAsset} */ asset)}
            {#await sleep() then}
              {@const { id, previewURL, description, kind: _kind } = asset}
              <SimpleImageGridItem
                value={id}
                ariaLabel={description}
                {viewType}
                {multiple}
                selected={isSelected(asset)}
                onChange={({ detail: { selected } }) => {
                  onSelectionChange(asset, selected);
                }}
              >
                <AssetPreview
                  kind={_kind}
                  src={previewURL}
                  alt={description}
                  variant="tile"
                  crossorigin="anonymous"
                />
                {#if viewType === 'list' || (!env.isSmallScreen && !isStockAssets)}
                  <!-- The path is relative to the folder being browsed -->
                  <AssetPath
                    {...isStockAssets
                      ? { caption: description }
                      : { path: browsing ? getRelativePath(description, dirPath) : description }}
                  />
                {/if}
              </SimpleImageGridItem>
            {/await}
          {/snippet}
        </InfiniteScroll>
      </SimpleImageGrid>
    </div>
  {/if}
{/snippet}

{#if hasAuthInfo}
  {#if error}
    <EmptyState>
      <span role="alert">{_(`assets_dialog.error.${error}`)}</span>
    </EmptyState>
  {:else if upload}
    <DropZone accept={fieldConfig?.accept} multiple onDrop={({ files }) => uploadFiles(files)}>
      {@render content()}
    </DropZone>
  {:else}
    {@render content()}
  {/if}
{:else if hasConfig}
  <CloudServiceAuth {serviceProps} onAuth={loadAuthInfo} />
{:else}
  <EmptyState>
    <span role="alert">{_('cloud_storage.invalid')}</span>
  </EmptyState>
{/if}

<Toast bind:show={uploadingToast.show}>
  <Alert status={uploadingToast.status}>
    {#if uploadingToast.status === 'info'}
      {_('uploading_files_progress')}
    {/if}
    {#if uploadingToast.status === 'error'}
      {_('uploading_files_failed')}
    {/if}
  </Alert>
</Toast>

<RejectedFilesAlertDialog bind:this={rejectedFilesAlert} {maxSize} />

<Toast bind:show={folderCreationFailed}>
  <Alert status="error">{_('creating_folder_failed')}</Alert>
</Toast>

{#if createFolder}
  <SubfolderNameDialog
    bind:open={newFolderDialogOpen}
    title={_('new_folder')}
    okLabel={_('new_folder_create')}
    description={_('new_folder_description', { values: { folder: folderLabel } })}
    {takenNames}
    onSubmit={(name) => {
      createNewFolder(name);
    }}
  />
{/if}

<style>
  .grid-wrapper {
    overflow-y: auto;
    height: 100%;
  }
</style>
